import { ItemTemplate, TemplateElement, ViewElement } from '@mobileTypes'

// Get element by path
export const getElementByPath = (
  path: string[],
  template: ItemTemplate
): TemplateElement | null => {
  if (path.length === 0) return null
  let current: any = template
  for (const idx of path) {
    const numIdx = parseInt(idx)
    if (current.children && current.children[numIdx]) {
      current = current.children[numIdx]
    } else {
      return null
    }
  }

  return current as TemplateElement
}

// Get parent items array by parent path
export const getParentItems = (
  parentPath: string[] | undefined,
  template: ItemTemplate
): TemplateElement[] => {
  if (parentPath === undefined || parentPath.length === 0) {
    return template.children || []
  }
  let current: any = template
  for (const idx of parentPath) {
    const numIdx = parseInt(idx)
    if (current.children && current.children[numIdx]) {
      current = current.children[numIdx]
    } else {
      return []
    }
  }
  if (current.type === 'view') {
    return (current as ViewElement).children || []
  }

  return []
}

// Get parent path from element path
export const getParentPath = (elementPath: string[]): string[] => {
  return elementPath.slice(0, -1)
}

// Get parent ID from element path
export const getParentId = (elementPath: string[], template: ItemTemplate): string | undefined => {
  const parentPath = getParentPath(elementPath)
  if (parentPath.length === 0) {
    return undefined
  }
  const parent = getElementByPath(parentPath, template)

  return parent ? parentPath.join(':') : undefined
}

// Get parent array (the array containing the element)
export const getParentArray = (
  elementPath: string[],
  template: ItemTemplate
): TemplateElement[] => {
  const parentPath = getParentPath(elementPath)

  return getParentItems(parentPath, template)
}

// Update template with new items at parent path
export const updateTemplate = (
  template: ItemTemplate,
  parentPath: string[] | undefined,
  items: TemplateElement[]
): ItemTemplate => {
  const updated = { ...template }
  if (parentPath === undefined || parentPath.length === 0) {
    updated.children = items

    return updated
  }

  let current: any = updated
  for (let i = 0; i < parentPath.length; i++) {
    const idx = parseInt(parentPath[i])
    if (current.children && current.children[idx]) {
      if (i === parentPath.length - 1) {
        // Last level - update children
        const targetElement = current.children[idx]
        if (targetElement.type === 'view') {
          current.children[idx] = { ...targetElement, children: items }
        }
      } else {
        current = current.children[idx]
      }
    }
  }

  return updated
}

export const removeElement = (template: ItemTemplate, elementPath: string[]): ItemTemplate => {
  const parentPath = getParentPath(elementPath)
  const parentItems = getParentItems(parentPath, template)
  const elementIndex = parseInt(elementPath[elementPath.length - 1])
  const newItems = parentItems.filter((_, idx) => idx !== elementIndex)

  return updateTemplate(template, parentPath, newItems)
}

export const addElement = (
  template: ItemTemplate,
  parentPath: string[] | undefined,
  element: TemplateElement,
  index?: number
): ItemTemplate => {
  const parentItems = getParentItems(parentPath, template)
  const newItems = [...parentItems]
  if (index !== undefined) {
    newItems.splice(index, 0, element)
  } else {
    newItems.push(element)
  }

  return updateTemplate(template, parentPath, newItems)
}

// Flatten all items for easier searching
export const flattenItems = (items: TemplateElement[]): TemplateElement[] => {
  return items.reduce((result: TemplateElement[], item: TemplateElement) => {
    result.push(item)
    if (item.type === 'view' && item.children && item.children.length > 0) {
      result = result.concat(flattenItems(item.children))
    }

    return result
  }, [])
}
